import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {AbortController | null} */
  #serverSyncAbort = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #serverSyncTimer = null;

  /** @type {Promise<void> | null} */
  #animationInFlight = null;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:cart:update', this.onShopifyCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    // Source of truth on mount: read the current count from the live cart, not from
    // sessionStorage. Avoids stale counts persisting across tabs/sessions.
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('shopify:cart:update', this.onShopifyCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);

    this.#serverSyncAbort?.abort();
    if (this.#serverSyncTimer) clearTimeout(this.#serverSyncTimer);
  }

  /**
   * Restored from bfcache — cart could have changed in another tab. Resync.
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) this.#scheduleServerSync(0);
  };

  /**
   * Shopify reloads the header section (theme editor, app blocks, etc). The DOM is
   * re-rendered server-side with the latest cart, but resync to be safe in case the
   * editor is operating on a stale cart.
   * @param {Event} event
   */
  onSectionLoad = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target && target.contains?.(this)) this.#scheduleServerSync(0);
  };

  /**
   * Third-party apps (subscriptions, upsells, custom AJAX cart actions, etc) dispatch
   * `shopify:cart:update`. Treat as a hint and pull the truth from /cart.js.
   */
  onShopifyCartUpdate = () => {
    this.#scheduleServerSync(0);
  };

  /**
   * Handles theme cart events. Both `CartAddEvent` (from product-form-component) and
   * `CartUpdateEvent` (from cart-items-component, etc.) fire under the same event name.
   *
   * They use `event.detail.data.itemCount` with *different semantics*:
   *  - product-form-component  → itemCount is the delta being added.
   *  - everything else         → itemCount is the new total cart count.
   *
   * Apply an optimistic update for instant feedback, then reconcile against /cart.js
   * (the only true source). This avoids drift, races, and the "badge appears then
   * disappears" symptom caused by trusting payload values alone.
   *
   * @param {CartUpdateEvent} event
   */
  onCartUpdate = (event) => {
    const optimisticTotal = this.#optimisticTotalFromEvent(event);
    if (optimisticTotal != null) {
      this.#renderCount(optimisticTotal, { animate: true });
    }

    // Reconcile shortly after — long enough to coalesce bursts of events from a
    // single user action, short enough to feel instant.
    this.#scheduleServerSync(75);
  };

  /**
   * @param {CartUpdateEvent} event
   * @returns {number | null}
   */
  #optimisticTotalFromEvent(event) {
    const data = event?.detail?.data;
    if (!data || data.didError) return null;

    const itemCount = typeof data.itemCount === 'number' ? data.itemCount : null;
    if (itemCount == null) return null;

    const resource = /** @type {{ item_count?: number }} */ (event.detail?.resource);
    if (resource && typeof resource.item_count === 'number') {
      return Math.max(0, resource.item_count);
    }

    if (data.source === 'product-form-component') {
      return Math.max(0, this.currentCartCount + itemCount);
    }

    return Math.max(0, itemCount);
  }

  /**
   * Public entry point used on mount and from external callers — fetch the current
   * cart from the server and reflect it in the badge.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;
    this.#scheduleServerSync(0);
  };

  /**
   * Coalesce server syncs so a burst of events results in a single network call.
   * @param {number} delayMs
   */
  #scheduleServerSync(delayMs) {
    if (this.#serverSyncTimer) clearTimeout(this.#serverSyncTimer);
    this.#serverSyncTimer = setTimeout(() => {
      this.#serverSyncTimer = null;
      this.#syncFromServer();
    }, delayMs);
  }

  async #syncFromServer() {
    try {
      this.#serverSyncAbort?.abort();
      const controller = new AbortController();
      this.#serverSyncAbort = controller;

      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
        credentials: 'same-origin',
      });
      if (!response.ok) return;

      const data = await response.json();
      const itemCount = typeof data?.item_count === 'number' ? data.item_count : 0;
      this.#renderCount(itemCount, { animate: false });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // Network failure — keep whatever optimistic state we already have.
    }
  }

  /**
   * Single point that mutates the badge DOM. Drives show/hide off the actual count
   * (not the event payload) so a stray event with `itemCount: 0` can't accidentally
   * hide a non-empty cart.
   *
   * @param {number} itemCount
   * @param {{ animate: boolean }} options
   */
  #renderCount(itemCount, { animate }) {
    const safe = Math.max(0, Number.isFinite(itemCount) ? Math.floor(itemCount) : 0);
    const previous = this.currentCartCount;
    const isEmpty = safe === 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', isEmpty);
    this.refs.cartBubble.classList.toggle('visually-hidden', isEmpty);
    this.classList.toggle('header-actions__cart-icon--has-cart', !isEmpty);

    this.currentCartCount = safe;

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({ value: String(safe), timestamp: Date.now() })
    );

    if (!animate || isEmpty || safe === previous) return;

    this.#playBubbleAnimation();
  }

  async #playBubbleAnimation() {
    if (this.#animationInFlight) return;

    const run = (async () => {
      // Wait one frame so the visibility changes commit before the animation begins.
      await new Promise((resolve) => requestAnimationFrame(resolve));
      this.refs.cartBubble.classList.add('cart-bubble--animating');
      try {
        await onAnimationEnd(this.refs.cartBubbleText);
      } finally {
        this.refs.cartBubble.classList.remove('cart-bubble--animating');
      }
    })();

    this.#animationInFlight = run;
    try {
      await run;
    } finally {
      this.#animationInFlight = null;
    }
  }
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
